/**
 * Stage Plot Editor — Main component.
 *
 * Layout: [ Toolbar (left) | Canvas (center) | Properties (right) ]
 * Self-contained: takes initial StagePlot JSON, emits onChange with the
 * updated plot.
 */
import { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import type { StagePlot, StageElement, PaletteCategory } from '../../shared/types/stageplot';
import { createEmptyStagePlot } from '../../shared/types/stageplot';
import { useUndoableReducer } from './useStagePlotStore';
import Toolbar from './Toolbar';
import StagePlotCanvas from './StagePlotCanvas';
import PropertiesSidebar from './PropertiesSidebar';
import RiserEditor from './RiserEditor';
import ErrorBoundary from './ErrorBoundary';
import { type RiserConfig } from './riserConfig';
import { normalizeElementConfig, normalizePlotElementConfigs } from '../../lib/stageplotElements';
import { buildElementForAdd, arrangeElements, type StageCommandAction } from '../../lib/stageCommand';
import './StagePlotEditor.css';

export interface StagePlotEditorProps {
  initialJSON?: StagePlot;
  onChange?: (plot: StagePlot) => void;
  className?: string;
}

/** Imperative handle so the parent page can inject a generated draft. */
export interface StagePlotEditorHandle {
  loadDraft: (plot: StagePlot) => void;
  /** Append elements to the current plot (used by the AI mapping confirm). */
  addElements: (elements: StageElement[]) => void;
  /** Apply validated assistant actions to the store (undoable). */
  applyActions: (actions: StageCommandAction[]) => void;
}

const StagePlotEditor = forwardRef<StagePlotEditorHandle, StagePlotEditorProps>(function StagePlotEditor(
  {
    initialJSON,
    onChange,
    className,
  }: StagePlotEditorProps,
  ref,
) {
  const { t } = useTranslation();
  // Normalize composite configs at construction too — the reducer's initial
  // state bypasses LOAD_PLOT, so a config-less composite would crash the first
  // paint before the mount effect runs.
  const initial = { ...(initialJSON ?? createEmptyStagePlot()), elements: normalizePlotElementConfigs((initialJSON ?? createEmptyStagePlot()).elements) };
  const { state, dispatch, undo, redo } = useUndoableReducer(initial);
  const [activeCategory, setActiveCategory] = useState<PaletteCategory>('drums');
  const [selectedColor, setSelectedColor] = useState('#4A90D9');
  const [zoom, setZoom] = useState(1);
  const [snapEnabled, setSnapEnabled] = useState(true);
  const [editingRiserId, setEditingRiserId] = useState<string | null>(null);
  const exportRef = useRef<(() => void)>(() => {});

  useImperativeHandle(ref, () => ({
      loadDraft: (plot: StagePlot) => {
        dispatch({ type: 'LOAD_PLOT', plot });
        setEditingRiserId(null);
      },
      addElements: (elements: StageElement[]) => {
              for (const el of elements) {
                // Single normalization point: composite icons always carry a
                // valid config before entering the store.
                dispatch({ type: 'ADD_ELEMENT', element: normalizeElementConfig(el) });
              }
            },
            applyActions: (actions: StageCommandAction[]) => {
              const stage = state.plot.stageDimensions;
              for (const action of actions) {
                if (action.op === 'add') {
                  const el = buildElementForAdd(action, stage);
                  if (!el) continue;
                  dispatch({ type: 'ADD_ELEMENT', element: normalizeElementConfig(el) });
                } else if (action.op === 'move') {
                  dispatch({ type: 'MOVE_ELEMENT', id: action.id, x: action.x, y: action.y });
                } else if (action.op === 'rotate') {
                  dispatch({ type: 'ROTATE_ELEMENT', id: action.id, rotation: action.rotation });
                } else if (action.op === 'remove') {
                  dispatch({ type: 'DELETE_ELEMENT', id: action.id });
                } else if (action.op === 'arrange') {
                  const arranged = arrangeElements(state.plot.elements, action.preset, stage);
                  const deltas = arranged
                    .map((el, i) => {
                      const orig = state.plot.elements[i];
                      if (!orig) return null;
                      return { id: el.id, x: el.x, y: el.y };
                    })
                    .filter((d): d is { id: string; x: number; y: number } => d !== null);
                  if (deltas.length) dispatch({ type: 'MOVE_ELEMENTS', deltas });
                }
              }
            },
          }), [dispatch, state.plot.elements, state.plot.stageDimensions]);

  useEffect(() => {
    if (initialJSON) dispatch({ type: 'LOAD_PLOT', plot: initialJSON });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Notify parent of changes.
  useEffect(() => {
    if (onChange) onChange(state.plot);
  }, [state.plot, onChange]);

  const selectedElement = state.selectedId
    ? (state.plot.elements.find((el) => el.id === state.selectedId) ?? null)
    : null;

  const handleAddElement = useCallback(
    (el: StageElement) => {
      dispatch({ type: 'ADD_ELEMENT', element: normalizeElementConfig(el) });
      if (el.iconId === 'risers') setEditingRiserId(el.id);
    },
    [dispatch],
  );
  const handleEditRiser = useCallback((id: string) => setEditingRiserId(id), []);
  const handleRiserChange = useCallback(
    (cfg: RiserConfig) =>
      editingRiserId && dispatch({ type: 'UPDATE_ELEMENT', id: editingRiserId, patch: { config: cfg as unknown as Record<string, unknown> } }),
    [editingRiserId, dispatch],
  );
  const handleMoveElement = useCallback(
    (id: string, x: number, y: number) => dispatch({ type: 'MOVE_ELEMENT', id, x, y }),
    [dispatch],
  );
  const handleResizeElement = useCallback(
    (id: string, width: number, height: number) =>
      dispatch({ type: 'RESIZE_ELEMENT', id, width, height }),
    [dispatch],
  );
  const handleRotateElement = useCallback(
    (id: string, rotation: number) => dispatch({ type: 'ROTATE_ELEMENT', id, rotation }),
    [dispatch],
  );
  const handleUpdateElement = useCallback(
    (id: string, patch: Partial<StageElement>) =>
      dispatch({ type: 'UPDATE_ELEMENT', id, patch }),
    [dispatch],
  );
  // Apply a patch to every selected element (used for group colour / flip changes).
  const handleUpdateElements = useCallback(
    (ids: string[], patch: Partial<StageElement>) => {
      for (const id of ids) dispatch({ type: 'UPDATE_ELEMENT', id, patch });
    },
    [dispatch],
  );
  const handleDuplicateElement = useCallback(
    (id: string) => dispatch({ type: 'DUPLICATE_ELEMENT', id }),
    [dispatch],
  );
  const handleReorderElement = useCallback(
    (id: string, place: 'front' | 'back' | 'forward' | 'backward') =>
      dispatch({ type: 'REORDER_ELEMENT', id, place }),
    [dispatch],
  );
  const handleSelectElement = useCallback(
    (id: string | null) => dispatch({ type: 'SELECT_ELEMENT', id }),
    [dispatch],
  );
  const handleSelectElements = useCallback(
    (ids: string[]) => dispatch({ type: 'SELECT_ELEMENTS', ids }),
    [dispatch],
  );
  const handleMoveElements = useCallback(
    (deltas: { id: string; x: number; y: number }[]) => dispatch({ type: 'MOVE_ELEMENTS', deltas }),
    [dispatch],
  );

  // Keyboard shortcuts.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;
      if ((e.key === 'Delete' || e.key === 'Backspace') && state.selectedIds.length > 0) {
        e.preventDefault();
        dispatch({ type: 'DELETE_ELEMENT', id: state.selectedIds[0] });
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        if (state.selectedIds.length > 0) dispatch({ type: 'DUPLICATE_ELEMENT', id: state.selectedIds[0] });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state.selectedId, dispatch, undo, redo]);

  const stageSizeChanged = useCallback(
    (w: number, h: number) => dispatch({ type: 'SET_STAGE', width: w, height: h }),
    [dispatch],
  );

  return (
    <div className={`stageplot-editor ${className ?? ''}`}>
      <Toolbar
        activeCategory={activeCategory}
        onCategoryChange={setActiveCategory}
        selectedColor={selectedColor}
        onColorChange={setSelectedColor}
        onAddElement={handleAddElement}
        stageWidth={state.plot.stageDimensions.width}
        stageHeight={state.plot.stageDimensions.height}
        onStageSizeChange={stageSizeChanged}
        onDeleteSelected={() => state.selectedIds.length > 0 && dispatch({ type: 'DELETE_ELEMENT', id: state.selectedIds[0] })}
        hasSelection={selectedElement !== null}
        canUndo={state.canUndo}
        canRedo={state.canRedo}
        onUndo={undo}
        onRedo={redo}
        onDuplicateSelected={() => state.selectedIds.length > 0 && handleDuplicateElement(state.selectedIds[0])}
        snapEnabled={snapEnabled}
        onToggleSnap={setSnapEnabled}
      />

      <div className="stageplot-canvas-wrap">
        <div className="stageplot-zoom">
          <button className="btn-icon" onClick={() => setZoom((z) => Math.max(0.2, Math.round((z - 0.1) * 10) / 10))} aria-label={t('stagePlotEditor.zoomOut')}>−</button>
          <span>{Math.round(zoom * 100)}%</span>
          <button className="btn-icon" onClick={() => setZoom((z) => Math.min(3, Math.round((z + 0.1) * 10) / 10))} aria-label={t('stagePlotEditor.zoomIn')}>+</button>
          <button className="btn-icon" onClick={() => setZoom(1)} aria-label={t('stagePlotEditor.resetZoom')} title={t('stagePlotEditor.resetZoomTitle')}>⌂</button>
          <span className="stageplot-dim-label">
            {state.plot.stageDimensions.width}m × {state.plot.stageDimensions.height}m
          </span>
        </div>
        <ErrorBoundary label={t('stagePlotEditor.canvasError')}>
        <StagePlotCanvas
          plot={state.plot}
          selectedIds={state.selectedIds}
          zoom={zoom}
          onMoveElement={handleMoveElement}
          onMoveElements={handleMoveElements}
          onResizeElement={handleResizeElement}
          onRotateElement={handleRotateElement}
          onUpdateElement={handleUpdateElement}
          onAddElement={handleAddElement}
          onSelectElement={handleSelectElement}
          onSelectElements={handleSelectElements}
          snapEnabled={snapEnabled}
          onExport={(cb) => (exportRef.current = cb)}
        />
        </ErrorBoundary>
      </div>

      <PropertiesSidebar
        selectedElement={selectedElement}
        selectedIds={state.selectedIds}
        onUpdateElement={handleUpdateElement}
        onUpdateElements={handleUpdateElements}
        onDeleteElement={(id) => dispatch({ type: 'DELETE_ELEMENT', id })}
        onDuplicateElement={handleDuplicateElement}
        onReorderElement={handleReorderElement}
        onEditRiser={handleEditRiser}
      />

      {editingRiserId && selectedElement?.iconId === 'risers' && (
        <ErrorBoundary label={t('stagePlotEditor.riserError')}>
          <RiserEditor
            config={(selectedElement.config as unknown) as RiserConfig}
            color={selectedElement.color}
            onConfigChange={handleRiserChange}
            onClose={() => setEditingRiserId(null)}
          />
        </ErrorBoundary>
      )}
    </div>
  );
});

export default StagePlotEditor;
